import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  IconButton,
  Tooltip,
  TextField,
  Typography,
  Chip,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Импортируем популярные иконки
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StarIcon from '@mui/icons-material/Star';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SecurityIcon from '@mui/icons-material/Security';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PaymentIcon from '@mui/icons-material/Payment';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import BusinessIcon from '@mui/icons-material/Business';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import HotelIcon from '@mui/icons-material/Hotel';
import FlightIcon from '@mui/icons-material/Flight';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import TrainIcon from '@mui/icons-material/Train';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import SportsIcon from '@mui/icons-material/Sports';
import FitnessIcon from '@mui/icons-material/FitnessCenter';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import MovieIcon from '@mui/icons-material/Movie';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VideocamIcon from '@mui/icons-material/Videocam';
import MicIcon from '@mui/icons-material/Mic';
import HeadsetIcon from '@mui/icons-material/Headset';
import ComputerIcon from '@mui/icons-material/Computer';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import TabletIcon from '@mui/icons-material/Tablet';
import WifiIcon from '@mui/icons-material/Wifi';
import BluetoothIcon from '@mui/icons-material/Bluetooth';
import BatteryFullIcon from '@mui/icons-material/BatteryFull';
import PowerIcon from '@mui/icons-material/Power';

// Стилизованная иконка для предпросмотра
const StyledIconButton = styled(IconButton)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(0.5),
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
  },
}));

// Категории иконок
const iconCategories = {
  general: {
    name: 'Общие',
    icons: [
      { name: 'Home', component: HomeIcon, code: '🏠' },
      { name: 'Person', component: PersonIcon, code: '👤' },
      { name: 'Email', component: EmailIcon, code: '✉️' },
      { name: 'Phone', component: PhoneIcon, code: '📞' },
      { name: 'Location', component: LocationOnIcon, code: '📍' },
      { name: 'Calendar', component: CalendarTodayIcon, code: '📅' },
      { name: 'Time', component: AccessTimeIcon, code: '⏰' },
      { name: 'Star', component: StarIcon, code: '⭐' },
      { name: 'Heart', component: FavoriteIcon, code: '❤️' },
      { name: 'Like', component: ThumbUpIcon, code: '👍' },
    ]
  },
  actions: {
    name: 'Действия',
    icons: [
      { name: 'Share', component: ShareIcon, code: '🔗' },
      { name: 'Download', component: DownloadIcon, code: '⬇️' },
      { name: 'Upload', component: UploadIcon, code: '⬆️' },
      { name: 'Search', component: SearchIcon, code: '🔍' },
      { name: 'Settings', component: SettingsIcon, code: '⚙️' },
      { name: 'Add', component: AddIcon, code: '➕' },
      { name: 'Remove', component: RemoveIcon, code: '➖' },
      { name: 'Edit', component: EditIcon, code: '✏️' },
      { name: 'Delete', component: DeleteIcon, code: '🗑️' },
      { name: 'View', component: VisibilityIcon, code: '👁️' },
    ]
  },
  status: {
    name: 'Статусы',
    icons: [
      { name: 'Info', component: InfoIcon, code: 'ℹ️' },
      { name: 'Warning', component: WarningIcon, code: '⚠️' },
      { name: 'Error', component: ErrorIcon, code: '❌' },
      { name: 'Success', component: CheckCircleIcon, code: '✅' },
      { name: 'Cancel', component: CancelIcon, code: '🚫' },
      { name: 'Lock', component: LockIcon, code: '🔒' },
      { name: 'Unlock', component: LockOpenIcon, code: '🔓' },
      { name: 'Security', component: SecurityIcon, code: '🛡️' },
    ]
  },
  business: {
    name: 'Бизнес',
    icons: [
      { name: 'Shopping', component: ShoppingCartIcon, code: '🛒' },
      { name: 'Payment', component: PaymentIcon, code: '💳' },
      { name: 'Money', component: MonetizationOnIcon, code: '💰' },
      { name: 'Trending Up', component: TrendingUpIcon, code: '📈' },
      { name: 'Trending Down', component: TrendingDownIcon, code: '📉' },
      { name: 'Bar Chart', component: BarChartIcon, code: '📊' },
      { name: 'Business', component: BusinessIcon, code: '🏢' },
      { name: 'Work', component: WorkIcon, code: '💼' },
    ]
  },
  places: {
    name: 'Места',
    icons: [
      { name: 'School', component: SchoolIcon, code: '🏫' },
      { name: 'Hospital', component: LocalHospitalIcon, code: '🏥' },
      { name: 'Restaurant', component: RestaurantIcon, code: '🍽️' },
      { name: 'Hotel', component: HotelIcon, code: '🏨' },
      { name: 'Flight', component: FlightIcon, code: '✈️' },
      { name: 'Car', component: DirectionsCarIcon, code: '🚗' },
      { name: 'Train', component: TrainIcon, code: '🚆' },
      { name: 'Bus', component: DirectionsBusIcon, code: '🚌' },
    ]
  },
  entertainment: {
    name: 'Развлечения',
    icons: [
      { name: 'Sports', component: SportsIcon, code: '⚽' },
      { name: 'Fitness', component: FitnessIcon, code: '💪' },
      { name: 'Music', component: MusicNoteIcon, code: '🎵' },
      { name: 'Movie', component: MovieIcon, code: '🎬' },
      { name: 'Camera', component: PhotoCameraIcon, code: '📷' },
      { name: 'Video', component: VideocamIcon, code: '📹' },
      { name: 'Microphone', component: MicIcon, code: '🎤' },
      { name: 'Headset', component: HeadsetIcon, code: '🎧' },
    ]
  },
  technology: {
    name: 'Технологии',
    icons: [
      { name: 'Computer', component: ComputerIcon, code: '💻' },
      { name: 'Phone', component: PhoneAndroidIcon, code: '📱' },
      { name: 'Tablet', component: TabletIcon, code: '📱' },
      { name: 'WiFi', component: WifiIcon, code: '📶' },
      { name: 'Bluetooth', component: BluetoothIcon, code: '📶' },
      { name: 'Battery', component: BatteryFullIcon, code: '🔋' },
      { name: 'Power', component: PowerIcon, code: '⚡' },
    ]
  }
};

const IconPicker = ({ open, onClose, onIconSelect }) => {
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [searchTerm, setSearchTerm] = useState('');

  // Фильтрация иконок по поисковому запросу
  const filteredIcons = searchTerm
    ? Object.values(iconCategories).flatMap(category =>
        category.icons.filter(icon =>
          icon.name.toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : iconCategories[selectedCategory]?.icons || [];

  const handleIconClick = (icon) => {
    onIconSelect(icon);
    onClose();
  };

  const handleClose = () => {
    setSearchTerm('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Выберите иконку
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder="Поиск иконок..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
          />
          
          {!searchTerm && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Категории:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(iconCategories).map(([key, category]) => (
                  <Chip
                    key={key}
                    label={category.name}
                    onClick={() => setSelectedCategory(key)}
                    color={selectedCategory === key ? 'primary' : 'default'}
                    variant={selectedCategory === key ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={1}>
          {filteredIcons.map((icon, index) => {
            const IconComponent = icon.component;
            return (
              <Grid item key={index}>
                <Tooltip title={`${icon.name} (${icon.code})`}>
                  <StyledIconButton
                    onClick={() => handleIconClick(icon)}
                    size="medium"
                  >
                    <IconComponent />
                  </StyledIconButton>
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>

        {filteredIcons.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {searchTerm ? 'Иконки не найдены' : 'Выберите категорию'}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          Отмена
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IconPicker;
